import { useEffect, useState } from 'react';
import { Plus, Pencil, Trash2, Loader2, X } from 'lucide-react';
import { apiService } from '@/services/api';
import type { Squad } from '@/types/squad';

export function AdminSquadsPage() {
  const [squads, setSquads] = useState<Squad[]>([]);
  const [loading, setLoading] = useState(true);
  const [salvando, setSalvando] = useState(false);
  const [mostrarForm, setMostrarForm] = useState(false);
  const [editandoId, setEditandoId] = useState<string | null>(null);
  const [nome, setNome] = useState('');
  const [regiao, setRegiao] = useState('');
  const [descricao, setDescricao] = useState('');
  const [ativo, setAtivo] = useState(true);

  const carregar = async () => {
    setLoading(true);
    try {
      const res = await apiService.listarSquads({});
      if (res.success && res.data) {
        setSquads(Array.isArray(res.data) ? res.data : []);
      } else {
        setSquads([]);
      }
    } catch {
      setSquads([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    carregar();
  }, []);

  const abrirNovo = () => {
    setEditandoId(null);
    setNome('');
    setRegiao('');
    setDescricao('');
    setAtivo(true);
    setMostrarForm(true);
  };

  const abrirEdicao = (squad: Squad) => {
    setEditandoId(squad.id);
    setNome(squad.nome);
    setRegiao(squad.regiao ?? '');
    setDescricao(squad.descricao ?? '');
    setAtivo(squad.ativo);
    setMostrarForm(true);
  };

  const handleSalvar = async () => {
    if (!nome.trim()) return;
    setSalvando(true);
    try {
      const dados = {
        nome: nome.trim(),
        regiao: regiao.trim() || undefined,
        descricao: descricao.trim() || undefined,
        ativo,
      };
      const res = editandoId
        ? await apiService.atualizarSquad(editandoId, dados)
        : await apiService.criarSquad(dados);
      if (res.success) {
        setMostrarForm(false);
        await carregar();
      } else {
        alert(res.message || 'Erro ao salvar squad');
      }
    } catch (err) {
      console.error(err);
      alert('Erro ao salvar squad');
    } finally {
      setSalvando(false);
    }
  };

  const handleRemover = async (squad: Squad) => {
    if (!confirm(`Remover o squad "${squad.nome}"? Só é possível se não houver usuários/projetos vinculados.`)) return;
    try {
      const res = await apiService.removerSquad(squad.id);
      if (res.success) {
        await carregar();
      } else {
        alert(res.message || 'Erro ao remover squad');
      }
    } catch (err) {
      console.error(err);
      alert('Erro ao remover squad');
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-schmersal-gray-dark dark:text-white">Squads</h1>
          <p className="text-sm text-schmersal-gray-medium dark:text-schmersal-gray-light mt-1">
            Cada squad cobre uma região. Usuários enxergam apenas projetos e colegas do próprio squad; admins enxergam tudo.
          </p>
        </div>
        <button
          type="button"
          onClick={abrirNovo}
          className="inline-flex items-center px-4 py-2 bg-schmersal-blue text-white rounded-lg hover:bg-schmersal-blue-dark transition-colors"
        >
          <Plus className="h-5 w-5 mr-2" />
          Novo squad
        </button>
      </div>

      {mostrarForm && (
        <div className="bg-white dark:bg-schmersal-gray-darker rounded-lg shadow p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-schmersal-gray-dark dark:text-white">
              {editandoId ? 'Editar squad' : 'Novo squad'}
            </h2>
            <button
              type="button"
              onClick={() => setMostrarForm(false)}
              className="text-schmersal-gray-medium hover:text-schmersal-gray-dark dark:text-schmersal-gray-light dark:hover:text-white"
            >
              <X className="h-5 w-5" />
            </button>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-schmersal-gray-dark dark:text-white mb-1">Nome *</label>
              <input
                type="text"
                value={nome}
                onChange={(e) => setNome(e.target.value)}
                placeholder="Ex.: MG"
                className="w-full px-3 py-2 border border-schmersal-gray-light dark:border-schmersal-gray-dark rounded-lg bg-white dark:bg-schmersal-gray-dark text-schmersal-gray-dark dark:text-white"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-schmersal-gray-dark dark:text-white mb-1">Região</label>
              <input
                type="text"
                value={regiao}
                onChange={(e) => setRegiao(e.target.value)}
                placeholder="Ex.: Minas Gerais"
                className="w-full px-3 py-2 border border-schmersal-gray-light dark:border-schmersal-gray-dark rounded-lg bg-white dark:bg-schmersal-gray-dark text-schmersal-gray-dark dark:text-white"
              />
            </div>
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-schmersal-gray-dark dark:text-white mb-1">Descrição</label>
              <textarea
                value={descricao}
                onChange={(e) => setDescricao(e.target.value)}
                rows={2}
                className="w-full px-3 py-2 border border-schmersal-gray-light dark:border-schmersal-gray-dark rounded-lg bg-white dark:bg-schmersal-gray-dark text-schmersal-gray-dark dark:text-white"
              />
            </div>
            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="squad-ativo"
                checked={ativo}
                onChange={(e) => setAtivo(e.target.checked)}
                className="rounded border-schmersal-gray-light dark:border-schmersal-gray-dark"
              />
              <label htmlFor="squad-ativo" className="text-sm text-schmersal-gray-dark dark:text-white">Ativo</label>
            </div>
          </div>
          <div className="flex gap-3 mt-4">
            <button
              type="button"
              onClick={handleSalvar}
              disabled={salvando || !nome.trim()}
              className="px-4 py-2 bg-schmersal-blue text-white rounded-lg hover:bg-schmersal-blue-dark disabled:opacity-50 flex items-center gap-2"
            >
              {salvando && <Loader2 className="h-4 w-4 animate-spin" />}
              {editandoId ? 'Salvar' : 'Criar'}
            </button>
            <button
              type="button"
              onClick={() => setMostrarForm(false)}
              className="px-4 py-2 border border-schmersal-gray-light dark:border-schmersal-gray-dark rounded-lg text-schmersal-gray-dark dark:text-white hover:bg-schmersal-gray-light dark:hover:bg-schmersal-gray-dark"
            >
              Cancelar
            </button>
          </div>
        </div>
      )}

      <div className="bg-white dark:bg-schmersal-gray-darker rounded-lg shadow p-4">
        {loading ? (
          <div className="flex justify-center py-12">
            <Loader2 className="h-8 w-8 animate-spin text-schmersal-blue" />
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="w-full text-left">
                <thead>
                  <tr className="border-b border-schmersal-gray-light dark:border-schmersal-gray-dark">
                    <th className="py-3 px-2 font-semibold text-schmersal-gray-dark dark:text-white">Nome</th>
                    <th className="py-3 px-2 font-semibold text-schmersal-gray-dark dark:text-white">Região</th>
                    <th className="py-3 px-2 font-semibold text-schmersal-gray-dark dark:text-white">Usuários</th>
                    <th className="py-3 px-2 font-semibold text-schmersal-gray-dark dark:text-white">Projetos</th>
                    <th className="py-3 px-2 font-semibold text-schmersal-gray-dark dark:text-white">Ativo</th>
                    <th className="py-3 px-2 font-semibold text-schmersal-gray-dark dark:text-white w-24">Ações</th>
                  </tr>
                </thead>
                <tbody>
                  {squads.map((sq) => (
                    <tr key={sq.id} className="border-b border-schmersal-gray-light dark:border-schmersal-gray-dark">
                      <td className="py-3 px-2 font-medium text-schmersal-gray-dark dark:text-white">{sq.nome}</td>
                      <td className="py-3 px-2 text-schmersal-gray-dark dark:text-white">{sq.regiao ?? '—'}</td>
                      <td className="py-3 px-2 text-schmersal-gray-dark dark:text-white">{sq.contadores?.usuarios ?? 0}</td>
                      <td className="py-3 px-2 text-schmersal-gray-dark dark:text-white">{sq.contadores?.projetos ?? 0}</td>
                      <td className="py-3 px-2 text-schmersal-gray-dark dark:text-white">{sq.ativo ? 'Sim' : 'Não'}</td>
                      <td className="py-3 px-2">
                        <div className="flex items-center gap-1">
                          <button
                            type="button"
                            onClick={() => abrirEdicao(sq)}
                            className="p-2 text-schmersal-blue dark:text-schmersal-yellow hover:bg-schmersal-gray-light dark:hover:bg-schmersal-gray-dark rounded"
                            title="Editar"
                          >
                            <Pencil className="h-4 w-4" />
                          </button>
                          <button
                            type="button"
                            onClick={() => handleRemover(sq)}
                            className="p-2 text-red-500 hover:bg-schmersal-gray-light dark:hover:bg-schmersal-gray-dark rounded"
                            title="Remover"
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {squads.length === 0 && (
              <p className="py-8 text-center text-schmersal-gray-medium dark:text-schmersal-gray-light">
                Nenhum squad cadastrado.
              </p>
            )}
          </>
        )}
      </div>
    </div>
  );
}
